import { Component, OnInit, ViewChild, inject, DestroyRef } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { translate } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import { Permission } from 'src/app/models/principal/permission';
import { LobService } from 'src/app/services/api/lob.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { AddLineOfBusinessComponent } from './add-line-of-business/add-line-of-business.component';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import cloneDeep from 'lodash/cloneDeep';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { ToastrErrorService } from 'src/app/services/util/toastr-error.service';


const REQUIRED_PERMISSIONS = [
  Permission.ADD_LOB,
  Permission.EDIT_LOB,
  Permission.DELETE_LOB
];

@Component({
    selector: 'app-line-of-business',
    templateUrl: './line-of-business.component.html',
    styleUrls: ['./line-of-business.component.scss'],
    standalone: false
})
export class LineOfBusinessComponent  implements OnInit {
  @ViewChild(MatSort, {static: true}) sort;
private destroyRef = inject(DestroyRef);
  breadcrumbItems: BreadcrumbItem[];
  displayedColumns: string[] = ['name', 'displayName', 'tagLine', 'description', 'image', 'noOfSolutions', 'menu'];
  lobData: any;
  dataSource: any;
  showSpinner = true;
  paginationSize = 10;
  pageIndex = 0;
  showPagination = true;
  sortInfo: any;
  isReader: boolean;
  Permission = Permission;
  permissionsMap: { [key: string]: boolean };
  isFailed = false;


  constructor(private lobService: LobService, private toastrService: ToastrService, private sidenavService: SidenavService,
    private permissionsService: PermissionsService, private customToastrService: CustomToastrService, private dialog: MatDialog,
    private toastrErrorService: ToastrErrorService) {
    
  }

  ngOnInit(): void {
    this.permissionsMap = this.permissionsService.resolveUserPermissions(REQUIRED_PERMISSIONS);
    this.sidenavService.selectedMenu = 'LEFT_SIDE_NAV.LINE_OF_BUSINESS';
    this.breadcrumbItems = [];
    this.breadcrumbItems.push({ icon: 'assets/images/home_icon.svg' },
      { label: translate('LEFT_SIDE_NAV.SOLUTION_ONBOARDING') },
      { label: translate('LEFT_SIDE_NAV.LINE_OF_BUSINESS'), url: ['lob'] });
    this.getLineOfBuinsessData();
  }

  getLineOfBuinsessData(): void {
    this.showSpinner = true;
    this.isFailed = false;
    this.lobData = [];
    this.loadData(this.lobData);
    this.lobService.getLineOfBusiness()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data) {
          this.lobData = data;
          this.loadData(this.lobData);
          this.showSpinner = false;
        }
      },
      (error) => {
        this.showSpinner = false;
        this.isFailed = true;
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.customToastrService.errMessage(message, title);
        }, 500);
      });
  }

  loadData(data?): void {
    this.pageIndex = 0;
    if (this.sortInfo) {
      const sortedData = this.sortInstanceData(this.sortInfo);
      this.dataSource = new MatTableDataSource(sortedData?.data?.slice(this.pageIndex, this.paginationSize));
    } else {
      this.dataSource = new MatTableDataSource(data?.slice(this.pageIndex, this.paginationSize));
    }
  }

  sortData(event): void {
    this.sortInfo = event;
    this.showPagination = false;
    setTimeout(() => {
      this.showPagination = true;
    }, 10);
    this.pageIndex = 0;
    this.lobData = this.sortInstanceData(event);
    this.dataSource = new MatTableDataSource(this.lobData?.slice(this.pageIndex, this.paginationSize));
  }

  sortInstanceData(event): any {
    return this.lobData?.sort((a, b) => {
      let comparatorResult = 0;
      switch (event.active) {
        case 'name':
          comparatorResult = a?.name && b?.name ? a?.name?.localeCompare(b?.name) : !a?.name ? -1 : 1;
          break;
        case 'displayName':
          comparatorResult = a?.displayName && b?.displayName ? a?.displayName?.localeCompare(b?.displayName) : !a?.displayName ? -1 : 1;
          break;
        case 'tagLine':
          comparatorResult = a?.tagline && b?.tagline ? a?.tagline?.localeCompare(b?.tagline)
            : !a?.tagline ? -1 : 1;
          break;
        case 'description':
          comparatorResult = a?.shortDescription && b?.shortDescription ? a?.shortDescription?.localeCompare(b?.shortDescription)
            : !a?.shortDescription ? -1 : 1;
          break;
        case 'image':
          comparatorResult = a?.imageRef && b?.imageRef ? a?.imageRef?.localeCompare(b?.imageRef)
            : !a?.imageRef ? -1 : 1;
          break;
        case 'noOfSolutions':
          comparatorResult = a?.numOfSolutions &&
            b?.numOfSolutions ? a?.numOfSolutions?.toString()?.localeCompare(b?.numOfSolutions?.toString())
            : !a?.numOfSolutions ? -1 : 1;
          break;
        case 'menu':
          comparatorResult = a?.menu && b?.menu ? a?.menu?.localeCompare(b?.menu)
            : !a?.menu ? -1 : 1;
          break;
        default:
          comparatorResult = a?.name && b?.name ? a?.name?.localeCompare(b?.name) : !a?.name ? -1 : 1;
          break;
      }
      return comparatorResult * (event.direction === 'asc' ? 1 : -1);
     });
  }

  pageChanged(event): any {
    if (event.length) {
      this.pageIndex = event.pageIndex;
      this.paginationSize = event.pageSize;
      const firstIndex = this.pageIndex * this.paginationSize;
      const data = this.lobData.slice(firstIndex, firstIndex + this.paginationSize);
      this.dataSource = new MatTableDataSource(data);
    }
  }

  addNewClick(): void {
    this.sidenavService.openInRightSidenav(AddLineOfBusinessComponent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((formData) => {
        if (!formData) {
          return;
        }
      });
  }

  deleteLob(data): void {
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: translate('LINE_OF_BUSINESS.TITLE'),
        message: translate('LINE_OF_BUSINESS.MESSAGE'),
        confirmButtonText: translate('BUTTONS.DELETE')
      }
    })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirm => {
        if (confirm) {
          this.lobService.deletedLineOfBusiness(data.id)
            .pipe(
              takeUntilDestroyed(this.destroyRef))
            .subscribe((res) => {
              if (res) {
                setTimeout(() => {
                  const message = translate('LINE_OF_BUSINESS.LOB_DELETED');
                  const title = translate('INSTANCE_STATUS.lineOfBusiness');
                  this.customToastrService.successMessage(message, title);
                }, 500);
              }
            },
            (error) => {
              const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
              this.dialog.open(ErrorDialogComponent, {
                minHeight: '342px',
                width: '630px',
                panelClass: 'flex-dialog',
                data: {
                  title:  translate('ERROR_DIALOG.DELETE_LINE_OF_BUSINESS_FAILED'),
                  description: errorMessage
                }
              });
            });
        }
      });
  }

  editLob(data): void {
    const editData = cloneDeep(data);
    this.sidenavService.openInRightSidenav(AddLineOfBusinessComponent, { editData, edit: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((dataForm) => {
        if (!dataForm) {
          return;
        }
      });
  }
}
